import ClassNames from 'classnames';
import React, { Component, createRef } from 'react';

import type { Address } from '../../../../js/@types/Address';
import type { AddressSearchModel } from '../../../../js/@types/AddressSearchModel';
import { productArea } from '../../../../js/utils/productArea';
import SearchAddressActions from '../../actions/SearchAddressActions';
import { AddressSearchHandle } from '../addressSearch/AddressAutocomplete';
import AddressChangeTracker from '../addressSearch/AddressChangeTracker';
import AddressSearch, { AddressSearchProps } from '../addressSearch/AddressSearch';
import HeaderBrowse from './HeaderBrowse';
import HeaderSearch from './HeaderSearch';

interface Props {
    productAreaType: number;
    address: Address;
    addressData: AddressSearchModel;
    addressLabelPrefix: string;
    allowEdit: boolean;
    ignoreAddressChange: boolean;
    isMobilePhones: boolean;
    useBigTitle: boolean;
    nbnInfo: string;
    text: {
        countryName: string;
        title: string;
        subTitle: string;
    };
}

interface State {
    initialAddress: string;
    address: Address;
    hidden: boolean;
    inputValue: string;
    addressNotFound?: boolean;
    isError?: boolean;
}

const getLabel = (address: Address) => {
    return address?.streetNumber ? address.label : null;
};

const shouldHideAddressSearch = (productAreaType: number) =>
    productAreaType === productArea.mobilePhones || productAreaType === productArea.tablets;

export default class LocationHeader extends Component<Props, State> {
    resultsLocationForm: React.RefObject<HTMLFormElement>;
    addresSearchRef: React.RefObject<AddressSearchHandle>;

    constructor(props: Props) {
        super(props);

        const label = getLabel(props.address);
        this.state = {
            initialAddress: label,
            address: props.address || ({} as Address),
            hidden: !!props.address?.streetNumber || shouldHideAddressSearch(props.productAreaType),
            inputValue: label || undefined
        };

        // Set the address cookie because the search address component doesn't do it when it's hidden
        SearchAddressActions.setAddressCookie(props.address);

        this.resultsLocationForm = createRef();
        this.addresSearchRef = createRef();
    }

    static getDerivedStateFromProps = (props: Props, state: State) => {
        const label = getLabel(props.address);
        return label !== state.initialAddress
            ? {
                  initialAddress: label,
                  address: props.address || {},
                  inputValue: label || undefined
              }
            : null;
    };

    onAddressChange = (value: Address) => {
        //
        if ((value && this.state && this.state.address === value) || this.props.ignoreAddressChange) {
            return;
        }

        // Set the address cookie because the search address component doesn't do it when it's hidden
        if (this.state.hidden) {
            SearchAddressActions.setAddressCookie(value);
        }

        this.setState({
            address: value,
            inputValue: (value || {}).label || null,
            addressNotFound: null,
            isError: null
        });

        if (value) {
            this.hideAddressSearch(true);
        }
    };

    onAddressSelect = () => {
        if (this.state.address) {
            this.hideAddressSearch(true);
        }
    };

    handleAddressChanged = (address: Address) => {
        if (this.props.ignoreAddressChange) {
            return;
        }

        this.setState({
            address: address || ({} as Address),
            inputValue: (address || {}).label || null
        });
    };

    toggle = () => {
        if (this.state.hidden) {
            this.showAddressSearch();
        } else {
            this.setState(
                {
                    // Reset input value, otherwise the search bar wouldn't collapse if the address wasn't found
                    inputValue: null
                },
                () => {
                    this.hideAddressSearch();
                }
            );
        }
    };

    // onEnterPress() {
    //     this.hideAddressSearch();
    // }

    onAddressEmptyResults = () => {
        this.setState({
            addressNotFound: true,
            isError: null
        });
    };

    onAddressResponseError = () => {
        this.setState({
            addressNotFound: null,
            isError: true
        });
    };

    onInput = (value: string) => {
        this.setState({
            inputValue: value,
            addressNotFound: null,
            isError: null
        });
    };

    onInputInitialized = (input: HTMLInputElement) => {
        if (!input) {
            return;
        }
        input.value = this.state.inputValue || '';
    };

    showAddressSearch() {
        this.setState(
            {
                hidden: false,
                inputValue: (this.state.address || {}).label || null
            },
            () => {
                const searchAddress = this.addresSearchRef.current;
                wo$(this.resultsLocationForm.current).slideDown(400, () => {
                    searchAddress.focus();
                });
            }
        );
    }

    hideAddressSearch(force = false) {
        // Do not hide if the value is pending
        // (for example, in case of an error or if the address was not found)
        if (!force && this.state.inputValue && this.state.inputValue !== (this.state.address || {}).label) {
            return;
        }

        wo$(this.resultsLocationForm.current).slideUp(400, () => {
            this.setState({
                hidden: true,
                inputValue: null,
                addressNotFound: null,
                isError: null
            });
        });
    }

    renderErrorWarning(isError: boolean) {
        const classes = ClassNames('col-xs-24', { 'c-brand-danger': isError }, { 'c-brand-warning': !isError });

        const message = isError
            ? this.props.addressData.text.unableToVerifyAddress
            : this.props.addressData.text.addressNotFound;

        return (
            <div className="row pad-y-3 pad-x-3 text-center">
                <div className={classes}>
                    <span className="fa fa-exclamation-circle"></span> {message}
                </div>
            </div>
        );
    }

    // eslint-disable-next-line complexity
    render() {
        let addressLabel = this.state.address
            ? this.state.address.label || this.props.text.countryName
            : this.props.text.countryName;

        // If a state, display the long name
        if (this.state.address && this.state.address.state && addressLabel === this.state.address.state.shortName) {
            addressLabel = this.state.address.state.longName;
        }

        const addressLabelString =
            this.props.addressLabelPrefix && this.props.addressLabelPrefix.length > 0
                ? `${this.props.addressLabelPrefix} ${addressLabel}`
                : addressLabel;

        const addressData = this.props.addressData;

        const searchAddressProps: AddressSearchProps = {
            key: `${this.state.hidden}`,
            ref: this.addresSearchRef,
            className: 'form-group mar-0 pad-y-2',
            inputClassName: 'font-5',
            current: this.state.address,
            countryCode: addressData.countryCode,
            placeholder: addressData.text.addressSearchPlaceHolder,
            tooltipLink: <i dangerouslySetInnerHTML={{ __html: addressData.text.tooltipLink }} />,
            tooltipDescription: addressData.text.tooltipDescription,
            addressNotFoundMessage: null,
            commonErrorMessage: null,
            types: addressData.types,
            queryDelay: addressData.queryDelay,
            apiKey: addressData.apiKey,
            onChange: this.onAddressChange,
            onSelect: this.onAddressSelect,
            onEmptyResults: this.onAddressEmptyResults,
            onResponseError: this.onAddressResponseError,
            // onEnterPress: this.onEnterPress,
            onInput: this.onInput,
            onInitialized: this.onInputInitialized
        };

        const searchFormClasses = ClassNames({ 'display-hidden': this.state.hidden });

        const title = this.state.address?.streetNumber
            ? this.props.text.title
            : this.props.text.title.replace(/\s+at$/i, ' in');

        return (
            <React.StrictMode>
                <>
                    <AddressChangeTracker
                        onAddressChange={this.handleAddressChanged}
                        initialValue={this.state.address}
                    />
                    {this.props.useBigTitle ? (
                        <HeaderBrowse address={this.props.text.subTitle || addressLabelString} title={title} />
                    ) : (
                        <HeaderSearch
                            address={this.props.text.subTitle || addressLabelString}
                            allowEdit={this.props.allowEdit}
                            title={title}
                            nbnInfo={this.props.nbnInfo}
                            onEditClick={this.toggle}
                        />
                    )}
                    <form ref={this.resultsLocationForm} id="resultsLocationForm" className={searchFormClasses}>
                        <AddressSearch {...searchAddressProps} />
                    </form>
                    {!this.state.hidden && this.state.addressNotFound ? this.renderErrorWarning(false) : null}
                    {!this.state.hidden && this.state.isError ? this.renderErrorWarning(true) : null}
                </>
            </React.StrictMode>
        );
    }
}
